import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, Table2, CalendarDays, Receipt, PiggyBank, LogOut, Menu, X, Package, Warehouse, Scale, UploadCloud, Layers } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/rate-cards", label: "Rate Cards", icon: Table2, testid: "nav-rate-cards" },
  { to: "/dap-pricing", label: "DAP Pricing", icon: Layers, testid: "nav-dap" },
  { to: "/products", label: "Products", icon: Package, testid: "nav-products" },
  { to: "/sites", label: "Site Summary", icon: Warehouse, testid: "nav-sites" },
  { to: "/all-sites", label: "All-Site Summary", icon: LayoutDashboard, testid: "nav-all-sites" },
  { to: "/calendar", label: "Period Calendar", icon: CalendarDays, testid: "nav-calendar" },
  { to: "/cost-of-goods", label: "Cost of Goods", icon: Receipt, testid: "nav-cogs" },
  { to: "/savings", label: "Savings & Credits", icon: PiggyBank, testid: "nav-savings" },
  { to: "/reconciliation", label: "Reconciliation", icon: Scale, testid: "nav-reconciliation" },
  { to: "/import", label: "Import & Export", icon: UploadCloud, testid: "nav-import" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const SidebarInner = () => (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-5 h-16 border-b border-slate-800/60">
        <img src="/ep-logo-mark.png" alt="EP Group" className="h-8 w-auto" data-testid="sidebar-logo" />
        <div className="leading-tight border-l border-slate-700 pl-3">
          <p className="font-display font-bold text-white text-[13px]">Hub</p>
          <p className="text-[11px] text-slate-400">Financial Rec</p>
        </div>
      </div>
      <nav className="flex-1 px-3 py-5 space-y-1">
        {NAV.map((n) => {
          const Icon = n.icon;
          const active = location.pathname === n.to;
          return (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                active ? "bg-[#d4333b] text-white" : "text-slate-300 hover:bg-slate-800 hover:text-white"
              }`}
            >
              <Icon className="w-[18px] h-[18px]" />
              {n.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="px-3 py-4 border-t border-slate-800/60">
        <div className="px-3 pb-3">
          <p className="text-xs text-slate-400">Signed in as</p>
          <p className="text-sm text-white truncate" data-testid="sidebar-user-email">{user?.email}</p>
        </div>
        <Button
          variant="ghost"
          data-testid="logout-button"
          onClick={logout}
          className="w-full justify-start gap-3 text-slate-300 hover:bg-slate-800 hover:text-white"
        >
          <LogOut className="w-[18px] h-[18px]" /> Sign out
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-slate-900 flex-col fixed inset-y-0">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 bg-slate-900">
            <SidebarInner />
          </aside>
        </div>
      )}

      <div className="flex-1 lg:ml-64 min-w-0">
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between h-14 px-4 bg-slate-900 text-white">
          <div className="flex items-center gap-2">
            <img src="/ep-logo-mark.png" alt="EP Group" className="h-6 w-auto" />
            <span className="font-display font-bold">Hub</span>
          </div>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </header>
        <main className="p-4 sm:p-6 lg:p-8 max-w-[1500px] mx-auto animate-fade-up">{children}</main>
      </div>
    </div>
  );
}
